/**
 * Everything that goes stale when an interview link changes, invalidated in one place.
 *
 * Two callers edit links — the Interview Links page and the ⚙️ LinkEditorModal on a request card —
 * and they must invalidate identically or one of them quietly leaves stale state behind. That was
 * a live bug (Aug 2026): the modal cleared nothing, so a link repaired mid-reply left the board's
 * localStorage payload still reading "no interview link" through navigation and full reloads.
 *
 * What is invalidated, and why:
 * - the two bookability queries: editing a link is what FIXES what the at-risk banner flags, and
 *   the app-wide staleTime means no background refetch would save us.
 * - every cached board (`clearAllBoardCaches`): the board bakes `linkFound` into a payload that
 *   never refetches on its own. This client-side clear covers the editing browser immediately;
 *   `GET /payload-version` carries a links revision for every other browser.
 *
 * The list query itself (`['interview-links']`) is deliberately NOT here: the links page refetches
 * its list explicitly after each mutation, and the modal re-reads per open.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { clearAllBoardCaches } from '../taActionBoard/boardCache';

export function useRefreshAfterLinkChange(): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['interview-links-at-risk'] });
    void queryClient.invalidateQueries({ queryKey: ['interview-link-bookability'] });
    clearAllBoardCaches();
  }, [queryClient]);
}
